<template>
	<!-- data.loading covers both the delivery-address load and the PAT availability check, so only the progress
	     bar/shortage list (rendered below, outside the v-if="!data.loading" block) is visible until both finish.
	     The blocking overlay is suppressed while the check is active: it would cover the whole modal with a wait
	     cursor and make the stop button unclickable. -->
	<base-modal :loading="data.loading && !availabilityCheck.active"
	            :wide="true"
	            cancel-button-text=""
	            confirm-button-text=""
	            @cancel="close"
	            @confirm="confirm">

		<template v-slot:sidebar>
			<purchase-order-modal-sidebar />
		</template>

		<template v-slot:title>
			{{ t('shoppingcart.modal.createpo.content.title') }}
		</template>

		<template v-slot:default>
			<div ref="progressWrapperRef"
			     class="create-order-progress-wrapper"
			     :class="{'create-order-loading': data.loading}">
				<availability-check-progress v-if="availabilityCheck.active"
				                             :cancellable="!data.submitting"
				                             :checked="availabilityCheck.checked"
				                             :total="availabilityCheck.total"
				                             @stop="requestStopAvailabilityCheck" />

				<div v-if="availabilityCheck.shortages.length > 0" class="create-order-shortages">
					<availability-shortage-list :collapsed="!availabilityCheck.active"
					                            :shortages="availabilityCheck.shortages" />
					<div class="hr-line"></div>
				</div>

				<p v-if="data.loading && !availabilityCheck.active">
					{{ t('shoppingcart.generic.loading.text') }}
				</p>
			</div>
			<template v-if="!data.loading">

				<div v-if="hasRoleMasterOrSalesRep()" class="input-wrapper modal-wrapper__customer">
					<customer-select :required="true"
					                 @on-related-customer-change="resetDeliveryAddress" />
				</div>

				<delivery-address-select v-if="data.createNewSelected"
				                         :change-delivery-address-button-disabled="changeDeliveryAddressButtonDisabled"
				                         :delivery-addresses="filteredDeliveryAddresses"
				                         :selected-delivery-address-id="selectedDeliveryAddressId"
				                         @on-delivery-address-change="toggleDeliveryAddressModal" />

				<div v-if="hasRoleMasterOrSalesRep() || data.createNewSelected" class="hr-line"></div>

				<p class="modal-wrapper__disclaimer">
          {{ isSyngentaTenant ? t('site.syngenta.shoppingcart.modal.createpo.content.addPreferredDeliveryDay.disclaimer') : t('shoppingcart.modal.createpo.content.addPreferredDeliveryDay.disclaimer') }}
				</p>

				<delivery-day-select :preferred-delivery-day="data.preferredDeliveryDay"
				                     @on-preferred-delivery-day-change="setPreferredDeliveryDay" />

				<delivery-date-select :delivery-date-options="deliveryDateOptions"
				                      :selected-delivery-dates="data.selectedDeliveryDates"
				                      @on-selected-delivery-date-change="setSelectedDeliveryDates" />

				<div class="hr-line"></div>

				<nav class="modal-wrapper__action-tabs">
					<ul class="modal-wrapper__nav-list">
						<li :class="{'current': data.createNewSelected}"
						    @click="toggleAddOrCreate">
							{{ t('shoppingcart.modal.createpo.content.createNew.label') }}
						</li>

						<li :class="{'current': !data.createNewSelected}"
						    @click="toggleAddOrCreate">
							{{ t('shoppingcart.modal.createpo.content.addToExisting.label') }}
						</li>
					</ul>
				</nav>

				<new-order-form v-if="data.createNewSelected"
				                :is-add-note-selected="isAddNoteSelected"
				                :po-name="poName"
				                :po-note="poNote"
				                @onAddNoteToggle="toggleAddNoteSelected"
				                @on-po-name-change="setPoName"
				                @on-po-note-change="setPoNote" />

				<existing-order-select v-else
				                       :pre-selected-order-id="preSelectedOrderId"
				                       :selected-customer-id="selectedCustomerId"
				                       :selected-existing-purchase-order="data.selectedExistingPurchaseOrder"
				                       @on-selected-order-change="setSelectedOrder" />

			</template>
		</template>

		<template v-slot:button-confirm>
			<button :class="{ loading: data.submitting }"
			        :disabled="!valid"
			        class="button button--small button--green"
			        data-submit-order="true"
			        type="button"
			        @click="confirm">
				{{ t('shoppingcart.modal.createpo.content.submit.text') }}
				<span class="button-spinner"></span>
			</button>
		</template>
	</base-modal>

	<base-modal v-if="availabilityCheck.stopConfirmationOpen"
	            cancel-button-text=""
	            confirm-button-text=""
	            @cancel="resumeAvailabilityCheck">
		<template v-slot:title>
			{{ t('shoppingcart.availabilitycheck.confirm.title') }}
		</template>

		<p>{{ t('shoppingcart.availabilitycheck.confirm.text') }}</p>

		<template v-slot:buttons>
			<div class="availability-check-confirmation__buttons">
				<button class="button button--white button--small"
				        type="button"
				        @click="resumeAvailabilityCheck">
					{{ t('shoppingcart.availabilitycheck.confirm.continue.text') }}
				</button>
				<button class="button button--orange button--small"
				        type="button"
				        @click="confirmStopAvailabilityCheck">
					{{ t('shoppingcart.availabilitycheck.confirm.stop.text') }}
				</button>
			</div>
		</template>
	</base-modal>
</template>

<script lang="ts" setup>
	import BaseModal from '@/components/modals/BaseModal.vue';
	import AvailabilityCheckProgress from '@/components/AvailabilityCheckProgress.vue';
	import AvailabilityShortageList from '@/components/AvailabilityShortageList.vue';
	import CustomerSelect from '@/components/modals/purchaseordermodal/CustomerSelect.vue';
	import {type AvailabilityCheckResult, type AvailabilityShortage, updateAvailabilityFromPAT} from '@/service/availability.service';
	import {mapToSubmitOrderLine, orderLineIsSurplus} from '@/service/order.line.service';
	import {addToExistingPurchaseOrder, createPurchaseOrder} from '@/service/order.service';
	import {useDeliveryAddressStore} from '@/store/delivery.address.store';
	import {
		type DeliveryAddress,
		type DeliveryDate,
		deliveryDates,
		hasRoleMasterOrSalesRep,
		isOrderLinesInvalid,
		type Order,
		removeOrderLinesForDeliveryDates,
		shoppingCartStorage,
		type ShoppingCartOrderLine,
		type SubmitOrderLine,
		toggleRefreshAvailability
	} from 'dummen-shared';
	import {storeToRefs} from 'pinia';
	import {computed, nextTick, onBeforeMount, reactive, ref, watch} from 'vue';
	import {useI18n} from 'vue-i18n';
	import {getSelectedCustomer, resetSelectedCustomer} from 'dummen-shared/src/services/selected.customer.storage.service';
	import PurchaseOrderModalSidebar from '@/components/modals/purchaseordermodal/PurchaseOrderModalSidebar.vue';
	import DeliveryDaySelect from '@/components/modals/purchaseordermodal/DeliveryDaySelect.vue';
	import DeliveryDateSelect from '@/components/modals/purchaseordermodal/DeliveryDateSelect.vue';
	import NewOrderForm from '@/components/modals/purchaseordermodal/NewOrderForm.vue';
	import ExistingOrderSelect from '@/components/modals/purchaseordermodal/ExistingOrderSelect.vue';
	import type {DeliveryDateOption} from '@/types/DeliveryDateOption';
	import DeliveryAddressSelect from '@/components/modals/purchaseordermodal/DeliveryAddressSelect.vue';
	import {useToast} from 'vue-toastification';
	import {useCreateOrderStore} from '@/store/create.order.store.ts';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';

	const {t} = useI18n();
	const shoppingCartStore = useShoppingCartStore();
	const createOrderStore = useCreateOrderStore();
	const deliveryAddressStore = useDeliveryAddressStore();
	const {
		poName,
		poNote,
		selectedDeliveryAddressId,
		updateAvailability,
		preSelectedOrderId
	} = storeToRefs(createOrderStore);
	const notifier = useToast();
	const {deliveryAddresses} = storeToRefs(deliveryAddressStore);

	const agreedToTermsAndConditions = window.agreedToTermsAndConditions;

	const emits = defineEmits<{
		(e: 'close'): void
		(e: 'openCreatePurchaseOrderSuccessModel', poName: string): void
		(e: 'openChangeDeliveryAddressModal', deliveryAddresses: DeliveryAddress[]): void
	}>();


  type AvailabilityWindow = Window & {
    syngentaTenant?: boolean;
  };

  const availabilityWindow = window as AvailabilityWindow;
  const isSyngentaTenant = computed(() => availabilityWindow.syngentaTenant === true);

	interface CreatePurchaseOrderModal {
		submitting: boolean;
		selectedExistingPurchaseOrder: Order | null;
		selectedDeliveryDates: DeliveryDateOption[];
		createNewSelected: boolean | null;
		isAddNoteSelected: boolean;
		preferredDeliveryDay: number | null;
		loading: boolean;
		showChangeDeliveryAddressModel: boolean;
	}

	const data: CreatePurchaseOrderModal = reactive({
		submitting: false,
		selectedExistingPurchaseOrder: null,
		selectedDeliveryDates: [],
		createNewSelected: null,
		isAddNoteSelected: false,
		preferredDeliveryDay: null,
		loading: true,
		showChangeDeliveryAddressModel: false
	});

	const availabilityCheck = reactive({
		active: false,
		checked: 0,
		total: 0,
		shortages: [] as AvailabilityShortage[],
		stopConfirmationOpen: false
	});

	// Element wrapping the progress bar/shortage list, scrolled into view whenever a check (re)starts so it isn't
	// missed when confirm() re-triggers it while the user is already scrolled down near the submit button.
	const progressWrapperRef = ref<HTMLElement | null>(null);

	watch(() => availabilityCheck.active, (active) => {
		if (active) {
			nextTick(() => progressWrapperRef.value?.scrollIntoView({behavior: 'smooth', block: 'start'}));
		}
	});

	/**
	 * How long a successful check (no failed batches, no shortages) stays valid for skipping confirm()'s re-check.
	 * Short on purpose: just long enough to cover the time between the initial check finishing and the user
	 * clicking "Send order", not a general cache of availability data. Configurable per environment/build via
	 * VITE_RECHECK_FRESHNESS_MS (see .env) without needing a code change.
	 */
	const DEFAULT_RECHECK_FRESHNESS_MS = 30_000;
	const RECHECK_FRESHNESS_MS = Number(import.meta.env.VITE_RECHECK_FRESHNESS_MS) || DEFAULT_RECHECK_FRESHNESS_MS;

	let lastSuccessfulCheckSnapshot: string[] | null = null;
	let lastSuccessfulCheckTimestamp = 0;

	/**
	 * Builds keys for the order lines (and quantities) a live check covered. A recent full-cart check can be reused
	 * when it covered all lines currently selected for submission.
	 */
	const buildAvailabilityCheckSnapshot = (orderLines: ShoppingCartOrderLine[]): string[] => {
		return orderLines
			.filter(orderLine => !orderLineIsSurplus(orderLine.week, orderLine.year))
			.map(orderLine => JSON.stringify([
				orderLine.articleId,
				orderLine.formId,
				orderLine.locationId ?? null,
				orderLine.year,
				orderLine.week,
				orderLine.amount
			]))
			.sort()
	};

	let availabilityCheckAbortController: AbortController | null = null;
	let availabilityCheckDecision: Promise<boolean> | null = null;
	let resolveAvailabilityCheckDecision: ((continueChecking: boolean) => void) | null = null;

	const requestStopAvailabilityCheck = () => {
		if (availabilityCheckDecision) {
			return;
		}

		availabilityCheck.stopConfirmationOpen = true;
		availabilityCheckDecision = new Promise(resolve => {
			resolveAvailabilityCheckDecision = resolve;
		});
	};

	const resolveAvailabilityCheckPause = (continueChecking: boolean) => {
		availabilityCheck.stopConfirmationOpen = false;
		const resolveDecision = resolveAvailabilityCheckDecision;
		availabilityCheckDecision = null;
		resolveAvailabilityCheckDecision = null;
		resolveDecision?.(continueChecking);
	};

	const resumeAvailabilityCheck = () => {
		resolveAvailabilityCheckPause(true);
	};

	const confirmStopAvailabilityCheck = () => {
		availabilityCheckAbortController?.abort();
		resolveAvailabilityCheckPause(false);
	};

	const waitForAvailabilityCheckResume = async (): Promise<boolean> => {
		return availabilityCheckDecision ? availabilityCheckDecision : true;
	};

	const deliveryDateOptions = deliveryDates();

	const selectedCustomerId = computed(() => getSelectedCustomer());

	const filteredDeliveryAddresses = computed(() => {
		if (hasRoleMasterOrSalesRep()) {
			return selectedCustomerId.value
				? deliveryAddresses.value?.filter((deliveryAddress: DeliveryAddress) => deliveryAddress.customerId === selectedCustomerId.value)
				: [];
		}

		return deliveryAddresses.value;
	});

	const changeDeliveryAddressButtonDisabled = computed(() => {
		return !deliveryAddresses.value || deliveryAddresses.value.length === 0;
	});

	const isAddNoteSelected = computed(() => {
		return data.isAddNoteSelected || poNote.value?.length > 0;
	});

	const hasSomeValidDeliveryDatesSelected = computed(() => {
		if (!data.selectedDeliveryDates || data.selectedDeliveryDates.length === 0) {
			return false;
		}

		return data.selectedDeliveryDates.some((deliveryDate: DeliveryDate) => {
			const orderLinesForWeek = shoppingCartStore.getOrderLinesForWeek(deliveryDate.week, deliveryDate.year);
			return !isOrderLinesInvalid(orderLinesForWeek);
		});
	});

	const getOrderLinesForSelectedWeeks = computed((): ShoppingCartOrderLine[] => {
		return data.selectedDeliveryDates.flatMap((deliveryDate: DeliveryDate) => {
			const orderLinesForWeek = shoppingCartStore.getOrderLinesForWeek(deliveryDate.week, deliveryDate.year);
			if (isOrderLinesInvalid(orderLinesForWeek)) {
				return [];
			}
			return orderLinesForWeek;
		});
	});

	const hasIncorrectDeliveryDateValues = computed(() => {
		return !data.preferredDeliveryDay || !hasSomeValidDeliveryDatesSelected.value;
	});

	const isMasterOrSalesRepWithoutSelectedCustomer = computed(() => {
		return hasRoleMasterOrSalesRep() && !selectedCustomerId.value;
	});

	const valid = computed(() => {
		if (data.loading ||
			hasIncorrectDeliveryDateValues.value ||
			!agreedToTermsAndConditions ||
			isMasterOrSalesRepWithoutSelectedCustomer.value) {

			return false;
		}

		if (data.createNewSelected) {
			return selectedDeliveryAddressId.value && poName.value;
		}

		return data.selectedExistingPurchaseOrder !== null;
	});

	const toggleAddOrCreate = () => {
		data.createNewSelected = !data.createNewSelected;

		if (data.createNewSelected) {
			loadDeliveryAddresses();
		}
	};

	const toggleAddNoteSelected = () => {
		data.isAddNoteSelected = !data.isAddNoteSelected;

		if (!data.isAddNoteSelected) {
			createOrderStore.setPoNote('');
		}
	};

	const toggleDeliveryAddressModal = () => {
		emits('openChangeDeliveryAddressModal', filteredDeliveryAddresses.value);
	};

	const setPoName = (name: string) => createOrderStore.setPoName(name);

	const setPoNote = (note: string) => createOrderStore.setPoNote(note);

	const setMainDeliveryAddress = () => {
		const mainAddress = filteredDeliveryAddresses.value.find(address => address.mainAddress);
		createOrderStore.setSelectedDeliveryAddressId(mainAddress ? mainAddress.id : '');
	}

	const setSelectedDeliveryDates = (deliveryDates: DeliveryDateOption[]) => {
		data.selectedDeliveryDates = deliveryDates;
	}

	const setPreferredDeliveryDay = (deliveryDay: number) => {
		data.preferredDeliveryDay = deliveryDay;
		createOrderStore.setPreferredDeliveryDay(deliveryDay);
	}

	const setSelectedOrder = (order: Order | null) => {
		data.selectedExistingPurchaseOrder = createOrderStore.selectedExistingPurchaseOrder ?? order;
		createOrderStore.setSelectedExistingPurchaseOrder(order);
	}

	const resetDeliveryAddress = () => {
		setMainDeliveryAddress();
	}

	const deliveryAddressNotExist = () => {
		return !filteredDeliveryAddresses.value.some(address => address.id === selectedDeliveryAddressId.value);
	};

	const close = () => emits('close');

	const submitNewOrder = async (orderLines: ShoppingCartOrderLine[]) => {
		const submitData: {
			poName: string;
			deliveryAddressId: string;
			orderLines: SubmitOrderLine[];
			comment: string;
			preferredDeliveryDay?: number | null;
		} = {
			poName: poName.value.trim(),
			deliveryAddressId: selectedDeliveryAddressId.value,
			orderLines: orderLines.map(orderLine => mapToSubmitOrderLine(orderLine, data.preferredDeliveryDay)),
			comment: data.isAddNoteSelected ? poNote.value : '',
			preferredDeliveryDay: data.preferredDeliveryDay
		};

		return await createPurchaseOrder(submitData);
	};

	const submitAddToExistingPurchaseOrder = async (orderLines: ShoppingCartOrderLine[]) => {
		if (data.selectedExistingPurchaseOrder === null) {
			throw new Error('No existing purchase order selected');
		}

		const submitData = {
			orderLines: orderLines.map(orderLine => mapToSubmitOrderLine(orderLine, data.preferredDeliveryDay))
		};

		return await addToExistingPurchaseOrder(data.selectedExistingPurchaseOrder.applicationId, submitData);
	};

	const submitOrder = async (orderLines: ShoppingCartOrderLine[]) => {
		const response: { success: boolean } = data.createNewSelected ?
			await submitNewOrder(orderLines) :
			await submitAddToExistingPurchaseOrder(orderLines);

		if (!response?.success) {
			data.submitting = false;
			return;
		}

		resetSelectedCustomer();
		reset();

		close();

		removeOrderLinesForDeliveryDates(orderLines);

		if (window.plausible) {
			// Send custom event to Plausible.io
			window.plausible('checkout');
		}

		const name = data.selectedExistingPurchaseOrder ? data.selectedExistingPurchaseOrder.poName : poName.value;
		emits('openCreatePurchaseOrderSuccessModel', name);
	};

	const updateVarietyAvailability = async (orderLines?: ShoppingCartOrderLine[]): Promise<AvailabilityCheckResult | null> => {
		// Only update availability if the flag is set.
		if (!updateAvailability.value) {
			// Next time the modal is opened, we want to check the availability again.
			updateAvailability.value = true;
			return null;
		}

		availabilityCheck.active = true;
		availabilityCheck.checked = 0;
		availabilityCheck.total = 0;
		availabilityCheck.shortages = [];
		availabilityCheckAbortController = new AbortController();

		try {
			const result = await updateAvailabilityFromPAT({
				orderLines,
				onProgress: (checked, total) => {
					availabilityCheck.checked = checked;
					availabilityCheck.total = total;
				},
				onShortages: (shortages) => {
					availabilityCheck.shortages = [...availabilityCheck.shortages, ...shortages];
				},
				waitForBatch: waitForAvailabilityCheckResume,
				signal: availabilityCheckAbortController.signal
			});

			if (result.failed === 0 && !result.cancelled && result.shortages.length === 0) {
				lastSuccessfulCheckSnapshot = buildAvailabilityCheckSnapshot(
					orderLines ?? shoppingCartStorage.value.orderLines ?? []
				);
				lastSuccessfulCheckTimestamp = Date.now();
			} else {
				lastSuccessfulCheckSnapshot = null;
			}

			return result;

		} finally {
			availabilityCheck.active = false;
			availabilityCheckAbortController = null;

			// Send signal to update the availability overview.
			toggleRefreshAvailability();

			updateAvailability.value = true;
		}
	};

	const notifyAvailabilityCheckResult = (result: AvailabilityCheckResult | null) => {
		if (!result) {
			return;
		}

		if (result.failed > 0) {
			notifier.warning(t('shoppingcart.availabilitycheck.failed.text'));
		}

		if (result.cancelled) {
			notifier.warning(t('shoppingcart.availabilitycheck.cancelled.text'));
		}
	};

	const isLastCheckStillFresh = (orderLines: ShoppingCartOrderLine[]): boolean => {
		if (lastSuccessfulCheckSnapshot === null
			|| (Date.now() - lastSuccessfulCheckTimestamp) >= RECHECK_FRESHNESS_MS) {
			return false;
		}

		const checkedLines = new Set(lastSuccessfulCheckSnapshot);
		return buildAvailabilityCheckSnapshot(orderLines).every(orderLine => checkedLines.has(orderLine));
	};

	const confirm = async () => {
		if (!agreedToTermsAndConditions) {
			notifier.error(t('shoppingcart.user.notAgreedToTermsAndCondtions.text'));
			return;
		}

		if (data.submitting || availabilityCheck.active) {
			return;
		}

		data.submitting = true;

		// Last check for availability quantities in order to get better order success rate. Skipped when a check
		// just finished successfully for all lines being submitted: re-running it would only repeat the same
		// (possibly very slow, e.g. during a PAT outage) batches for no benefit.
		const orderLinesToSubmit = getOrderLinesForSelectedWeeks.value;
		if (!isLastCheckStillFresh(orderLinesToSubmit)) {
			const result = await updateVarietyAvailability(orderLinesToSubmit);
			notifyAvailabilityCheckResult(result);

			if (result?.cancelled || result?.shortages.length) {
				// The user stopped the check or there are shortages: let them adjust the order first.
				data.submitting = false;
				return;
			}
		}

		if (!valid.value) {
			data.submitting = false;
			return;
		}

		await submitOrder(orderLinesToSubmit);
	};

	const reset = () => {
		createOrderStore.reset();
	};

	const loadDeliveryAddresses = async () => {
		if (data.createNewSelected && (!deliveryAddresses.value || deliveryAddresses.value.length === 0)) {
			await deliveryAddressStore.initDeliveryAddresses();
		}

		if (!selectedDeliveryAddressId.value || deliveryAddressNotExist()) {
			setMainDeliveryAddress();
		}
	};

	const loadData = async () => {
		data.loading = true;
		data.createNewSelected = !preSelectedOrderId.value;
		data.selectedExistingPurchaseOrder = createOrderStore.selectedExistingPurchaseOrder;
		data.preferredDeliveryDay = createOrderStore.preferredDeliveryDay;

		// Only the progress bar/shortage list (rendered outside this data.loading block) should be visible while
		// the availability check runs; the rest of the form (customer/delivery-address/weeks) stays hidden until
		// both the delivery addresses and the availability check have finished, however long that takes.
		await Promise.all([
			loadDeliveryAddresses(),
			updateVarietyAvailability().then(notifyAvailabilityCheckResult)
		]);

		data.loading = false;
	};

	onBeforeMount(() => {
		loadData();
	});
</script>

<style scoped>
	.create-order-progress-wrapper {
		/* Transition (instead of an instant collapse) avoids a sudden ~420px layout shift right when the
		   availability check finishes and the form below becomes interactive: without it, a click made just
		   before/during the collapse lands on the old position instead of the newly-shown customer/delivery
		   address select. */
		min-height: 0;
		transition: min-height .3s ease;
	}

	.create-order-loading {
		min-height: 420px;
	}

	.availability-check-confirmation__buttons {
		display: flex;
		gap: 6px;
		justify-content: flex-end;
		margin-top: 10px;
		white-space: nowrap;
	}
</style>
